import { GRADIENTS } from '@pixee/design-system'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import invariant from 'tiny-invariant'
import { ParticleField } from '../components/particle-field.tsx'
import { TooltipWithHover } from '../components/tooltip.tsx'
import { AUTHENTIK_ADMIN_LOGIN_PATH } from '../constants.ts'
import { useGetSystemStatuses } from '../utils/api-client/api-hooks.ts'
import * as styles from './login-page.css.ts'

export type HealthCheck = { name: string; status: string }

// How long the health check may load before the indicator treats it as degraded. Bridges a normal
// fast response (no "Service degraded" flash) while still surfacing an unreachable server within a
// few seconds, rather than waiting for the browser's much longer default request timeout.
export const HEALTH_CHECK_GRACE_PERIOD_MS = 5_000

type ProductExample = {
  eyebrow: string
  metric: string
  headline: string
  body: string
  gradient: string
  tags: readonly string[]
  // Whether the live landing page rotates through this example. The full set is
  // kept for Storybook so every accent gradient stays showcased, but some
  // examples are held back from the production rotation (see PR #1713 review).
  isInRotation: boolean
}

// Examples alternate warm→cool→warm→cool→warm accent gradients (Heat → Veil →
// Rise → Set → Dawn), per the landing page design handoff.
export const PRODUCT_EXAMPLES: readonly ProductExample[] = [
  {
    eyebrow: 'RESOLUTION CENTER · ACTIVITY',
    metric: 'LIVE',
    headline: 'Watch findings get triaged and fixed in real time',
    body: 'As Pixee works through your backlog, each finding updates dynamically. Whether checking for exploitability or planning a fix, progress is visible without refreshing.',
    gradient: GRADIENTS.heat,
    tags: ['Live activity', 'Triage', 'Fixes'],
    isInRotation: true,
  },
  {
    eyebrow: 'INTEGRATIONS · DATADOG',
    metric: 'NEW',
    headline: 'Bring your Datadog findings into Pixee',
    body: 'Connect Datadog as a finding source and Pixee analyzes its results alongside your other scanners, then opens pull requests to fix what it can.',
    gradient: GRADIENTS.veil,
    tags: ['Datadog', 'SAST', 'SCA'],
    isInRotation: true,
  },
  {
    eyebrow: 'ACCESS · SINGLE SIGN-ON',
    metric: 'NEW',
    headline: 'Sign in with your own identity provider',
    body: 'Pixee connects to the identity provider your organization already uses, including Okta and Google Workspace, so your team signs in with the accounts they have.',
    gradient: GRADIENTS.rise,
    tags: ['SSO', 'Okta', 'Google Workspace'],
    isInRotation: false,
  },
  {
    eyebrow: 'SCA · DEPENDENCIES',
    metric: 'NEW',
    headline: 'Trace vulnerabilities deep into your dependencies',
    body: 'Pixee follows your dependency chains to surface not only the risks you declared directly, but those hiding in transitive, indirect packages as well.',
    gradient: GRADIENTS.set,
    tags: ['SCA', 'Dependencies', 'Supply chain'],
    isInRotation: true,
  },
  {
    eyebrow: 'DEPLOYMENT · NETWORKING',
    metric: 'NEW',
    headline: 'Runs cleanly inside locked-down networks',
    body: "In environments that inspect outbound traffic, Pixee trusts your corporate proxy's certificates so scans, fixes, and integrations keep working.",
    gradient: GRADIENTS.dawn,
    tags: ['On-prem', 'Proxy', 'Enterprise'],
    isInRotation: false,
  },
]

// Indices into PRODUCT_EXAMPLES that the live landing page rotates through.
const ROTATION_EXAMPLE_INDICES = PRODUCT_EXAMPLES.flatMap((example, index) => (example.isInRotation ? [index] : []))

const FEATURED_EXAMPLE_INDEX_STORAGE_KEY = 'login-page-featured-example-index'

// Each page load features the example after the previously shown one, so
// refreshing cycles through the in-rotation examples and their accent gradients.
// Will be driven by a release notes API once product-os PR #132 merges.
const getNextFeaturedExampleIndex = () => {
  const storedValue = localStorage.getItem(FEATURED_EXAMPLE_INDEX_STORAGE_KEY)
  const storedIndex = storedValue === null ? -1 : Number(storedValue)

  // A stored value that is no longer in rotation (a held-back example, or a
  // stale/invalid value) yields indexOf === -1, so the next position lands on
  // ROTATION_EXAMPLE_INDICES[0] — a fresh visitor starts at the first example.
  const previousRotationPosition = ROTATION_EXAMPLE_INDICES.indexOf(storedIndex)
  const nextRotationPosition = (previousRotationPosition + 1) % ROTATION_EXAMPLE_INDICES.length

  const nextIndex = ROTATION_EXAMPLE_INDICES[nextRotationPosition]
  invariant(nextIndex !== undefined, 'ROTATION_EXAMPLE_INDICES must not be empty')
  return nextIndex
}

const GradientText: React.FC<{ children: React.ReactNode; gradient: string }> = ({ children, gradient }) => (
  <span
    style={{
      backgroundImage: gradient,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      color: 'transparent',
      display: 'inline-block',
    }}
  >
    {children}
  </span>
)

const StatusIndicator: React.FC<{ isDown: boolean; failingChecks: readonly HealthCheck[] }> = ({
  isDown,
  failingChecks,
}) => {
  const indicator = (
    <div className={styles.statusIndicator({ isDown })}>
      <span className={styles.statusDotWrapper({ isDown })}>{!isDown && <span className={styles.statusPulse} />}</span>
      <span>{isDown ? 'Service degraded' : 'All systems operational'}</span>
    </div>
  )

  if (isDown) {
    return (
      <TooltipWithHover trigger={indicator} side="top">
        {failingChecks.length > 0 ? (
          <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
            {failingChecks.map(c => (
              <li key={c.name}>{c.name}</li>
            ))}
          </ul>
        ) : (
          <span>Unable to reach the system status service. Some systems may be unavailable.</span>
        )}
      </TooltipWithHover>
    )
  }

  return indicator
}

const BrandPanel: React.FC<{ featuredExampleIndex: number }> = ({ featuredExampleIndex }) => {
  const example = PRODUCT_EXAMPLES[featuredExampleIndex]
  invariant(example, 'featuredExampleIndex is out of bounds')

  return (
    <div className={styles.brandPanel}>
      <div className={styles.particleFieldWrapper}>
        <ParticleField density={1} intensity={0.8} />
      </div>

      <div className={styles.ambientGlow} />

      <div className={styles.brandTagline}>
        <div className={styles.taglineDot} />
        <span className={styles.taglineText}>Agentic security engineering</span>
      </div>

      <div className={styles.brandContent}>
        <div className={styles.technicalEyebrow}>{example.eyebrow}</div>
        <h1 className={styles.technicalHeadline}>
          <GradientText gradient={example.gradient}>{example.headline}</GradientText>
        </h1>
        <p className={styles.technicalBody}>{example.body}</p>
        <div className={styles.technicalTags}>
          {example.tags.map(tag => (
            <span key={tag} className={styles.technicalTag}>
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

const AuthCard: React.FC<{
  redirectPath: string | null
  isHealthDown: boolean
  failingHealthChecks: readonly HealthCheck[]
}> = ({ redirectPath, isHealthDown, failingHealthChecks }) => {
  const signInHref = redirectPath
    ? `/api/auth/login?${new URLSearchParams({ 'redirect-path': redirectPath }).toString()}`
    : '/api/auth/login'

  return (
    <div className={styles.authCard}>
      <div className={styles.authCardHeader}>
        <a href="/login" className={styles.logoLink} tabIndex={-1}>
          <img src="/Pixee_PrimaryLogo_Light.png" alt="Pixee Logo" className={styles.logoImageForDarkTheme} />
          <img src="/Pixee_PrimaryLogo_Dark.png" alt="Pixee Logo" className={styles.logoImageForLightTheme} />
        </a>
      </div>

      <div className={styles.authCardContent}>
        <div className={styles.selfHostedLabel}>Self-hosted</div>
        <h1 className={styles.authHeading}>Sign in to your Pixee instance</h1>
        <p className={styles.authDescription}>You'll be redirected to complete sign-in as needed.</p>
        <a href={signInHref} className={styles.continueButton}>
          Continue
        </a>
        {/* Break-glass route to a local Authentik admin (e.g. akadmin) when the configured IdP is
            broken and the default flow's auto-redirect cannot be completed. The login page is the
            only place this can live: the flow is declared `require_unauthenticated`, so Authentik
            refuses to run it for a request carrying a session. An equivalent link in the signed-in
            user menu would be rejected for everyone who could reach it (ISS-7780).
            Opens in a new tab despite being same-origin: the operator signs in to the flow, then
            returns to this still-open tab and clicks Continue, which now finds an Authentik
            session. Navigating in place would lose the redirect-path they arrived with. */}
        <a
          href={AUTHENTIK_ADMIN_LOGIN_PATH}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.adminLoginLink}
        >
          Sign in as a local administrator instead
        </a>
      </div>

      <div className={styles.authCardFooter}>
        <StatusIndicator isDown={isHealthDown} failingChecks={failingHealthChecks} />
      </div>
    </div>
  )
}

export type LoginPagePureProps = {
  redirectPath: string | null
  isHealthDown?: boolean
  failingHealthChecks?: readonly HealthCheck[]
  /** Index into PRODUCT_EXAMPLES selecting the featured release note and its accent gradient. */
  featuredExampleIndex?: number
}

export const LoginPagePure: React.FC<LoginPagePureProps> = ({
  redirectPath,
  isHealthDown = false,
  failingHealthChecks = [],
  featuredExampleIndex = 0,
}) => {
  // Lift the app-wide 855px body min-width while this full-screen page is shown
  // so it can render responsively on phones (see styles.responsiveBody).
  useEffect(() => {
    document.body.classList.add(styles.responsiveBody)
    return () => {
      document.body.classList.remove(styles.responsiveBody)
    }
  }, [])

  return (
    <div className={styles.pageContainer}>
      <div className={styles.brandPanelWrapper}>
        <BrandPanel featuredExampleIndex={featuredExampleIndex} />
      </div>
      <div className={styles.authCardColumn}>
        <AuthCard redirectPath={redirectPath} isHealthDown={isHealthDown} failingHealthChecks={failingHealthChecks} />
      </div>
    </div>
  )
}

export const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const redirectPath = searchParams.get('redirect-path')
  const { data, isError, isLoading } = useGetSystemStatuses({ refetchInterval: 30_000, retry: false })
  // We don't configure a request timeout, so an unreachable server leaves the query loading until
  // the browser's default timeout (~2 min). Treat the check as stalled once it has been loading
  // past the grace period so the indicator surfaces "degraded" within a few seconds instead.
  const [hasGracePeriodElapsed, setHasGracePeriodElapsed] = useState(false)
  useEffect(() => {
    const timeoutId = setTimeout(() => setHasGracePeriodElapsed(true), HEALTH_CHECK_GRACE_PERIOD_MS)
    return () => clearTimeout(timeoutId)
  }, [])

  const systemStatuses = data?._embedded?.items ?? []
  const failingHealthChecks: HealthCheck[] = systemStatuses
    .filter(system => system.status !== 'up')
    .map(system => ({ name: system.name, status: system.status }))
  // Surface "degraded" on a confirmed failure/down status, or once the check has stalled past the
  // grace period. A normal fast load settles within the window, so it never flashes "Service
  // degraded" before the first result arrives.
  const isHealthCheckStalled = isLoading && hasGracePeriodElapsed
  const isHealthDown = isError || failingHealthChecks.length > 0 || isHealthCheckStalled

  // The initializer only reads storage and the effect write is idempotent, so
  // StrictMode double-invocation cannot advance the rotation twice per visit.
  const [featuredExampleIndex] = useState(getNextFeaturedExampleIndex)

  useEffect(() => {
    localStorage.setItem(FEATURED_EXAMPLE_INDEX_STORAGE_KEY, String(featuredExampleIndex))
  }, [featuredExampleIndex])

  return (
    <LoginPagePure
      redirectPath={redirectPath}
      isHealthDown={isHealthDown}
      failingHealthChecks={failingHealthChecks}
      featuredExampleIndex={featuredExampleIndex}
    />
  )
}
